import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import { Power, Cpu, Mail, Copy, Check, ExternalLink, MessageSquare, Image as ImageIcon } from 'lucide-react';

const DEFAULT_CONFIG = {
  enabled: true,
  ai_first: true,
  require_email: false,
  welcome_message: '',
  assistant_name: 'KI-Assistent',
  no_context_reply: 'Dazu liegen mir aktuell keine gesicherten Informationen vor. Ich gebe Ihre Anfrage gerne an einen Mitarbeiter weiter.',
  escalation_label: 'Mit einem Mitarbeiter sprechen',
  escalation_online_message: 'Ich verbinde Sie mit einem Mitarbeiter. Bitte bleiben Sie kurz im Chat.',
  escalation_offline_message: 'Vielen Dank. Wir haben Ihre Anfrage erhalten und melden uns schnellstmöglich per E-Mail bei Ihnen.',
  escalation_email_required_message: 'Aktuell ist kein Mitarbeiter online. Bitte hinterlassen Sie Ihre E-Mail-Adresse, damit wir Ihre Anfrage beantworten können.',
  theme_color: '#EF694C',
  position: 'bottom-right',
  offline_message: '',
  launcher_image_url: '',
};

export default function Widget() {
  const { user } = useAuth();
  const { t } = useI18n();
  const tenantId = user?.tenant_id;

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [tenantSlug, setTenantSlug] = useState('');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return;

    api.get(`/tenants/${tenantId}/settings/widget`)
      .then((r) => {
        const widget = r.data?.widget_config || r.data || {};
        setConfig({ ...DEFAULT_CONFIG, ...widget });
        setTenantSlug(r.data?.tenant_slug || r.data?.slug || user?.tenant_slug || '');
      })
      .catch(() => toast.error(t.error || 'Fehler'));
  }, [tenantId, user?.tenant_slug, t.error]);

  const updateConfig = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!tenantId) return;

    setSaving(true);
    try {
      const payload = {
        enabled: Boolean(config.enabled),
        ai_first: Boolean(config.ai_first),
        require_email: Boolean(config.require_email),
        welcome_message: config.welcome_message || '',
        assistant_name: config.assistant_name || 'KI-Assistent',
        no_context_reply: config.no_context_reply || 'Dazu liegen mir aktuell keine gesicherten Informationen vor. Ich gebe Ihre Anfrage gerne an einen Mitarbeiter weiter.',
        escalation_label: config.escalation_label || 'Mit einem Mitarbeiter sprechen',
        escalation_online_message: config.escalation_online_message || 'Ich verbinde Sie mit einem Mitarbeiter. Bitte bleiben Sie kurz im Chat.',
        escalation_offline_message: config.escalation_offline_message || 'Vielen Dank. Wir haben Ihre Anfrage erhalten und melden uns schnellstmöglich per E-Mail bei Ihnen.',
        escalation_email_required_message: config.escalation_email_required_message || 'Aktuell ist kein Mitarbeiter online. Bitte hinterlassen Sie Ihre E-Mail-Adresse, damit wir Ihre Anfrage beantworten können.',
        theme_color: config.theme_color || '#EF694C',
        position: config.position || 'bottom-right',
        offline_message: config.offline_message || '',
        launcher_image_url: config.launcher_image_url || '',
      };

      const r = await api.put(`/tenants/${tenantId}/settings/widget`, payload);
      const widget = r.data?.widget_config || r.data || {};
      setConfig({ ...DEFAULT_CONFIG, ...widget });
      setTenantSlug(r.data?.tenant_slug || r.data?.slug || tenantSlug);

      toast.success(t.widgetSaved || 'Widget-Einstellungen gespeichert');
    } catch {
      toast.error(t.error || 'Fehler');
    } finally {
      setSaving(false);
    }
  };

  const widgetUrl = tenantSlug ? `${window.location.origin}/widget/${tenantSlug}` : '';
  const embedCode = tenantSlug
    ? `<script src="${window.location.origin}/widget-embed.js" data-tenant="${tenantSlug}"></script>`
    : '';

  const copyEmbed = () => {
    if (!embedCode) return;
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const toggles = [
    { key: 'enabled', icon: Power, label: t.enableWidget || 'Widget aktivieren', desc: t.enableWidgetDesc || 'Widget öffentlich verfügbar machen' },
    { key: 'ai_first', icon: Cpu, label: t.aiFirstMode || 'KI zuerst', desc: t.aiFirstModeDesc || 'KI beantwortet neue Anfragen zuerst' },
    { key: 'require_email', icon: Mail, label: t.requireEmail || 'E-Mail-Adresse abfragen', desc: t.requireEmailDesc || 'Besucher müssen vor dem Chat eine E-Mail-Adresse angeben' },
  ];

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="widget-settings-page">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-2xl text-white">{t.widgetTitle || 'Website-Widget'}</h2>
        <p className="text-sm text-[#a1a1aa] mt-1">{t.widgetDesc || 'Konfigurieren Sie das öffentliche Support-Widget.'}</p>
      </div>

      <div className="grid gap-6">
        <div className="bg-[#121214] border border-white/10 rounded-xl p-6 space-y-3">
          {toggles.map(({ key, icon: Icon, label, desc }) => (
            <button
              key={key}
              type="button"
              onClick={() => updateConfig(key, !config[key])}
              className="w-full flex items-center justify-between gap-4 p-4 bg-[#09090b] border border-white/10 rounded-lg text-left hover:border-white/20 transition-all"
              data-testid={`widget-toggle-${key}`}
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#EF694C]" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">{label}</span>
                  <span className="block text-xs text-[#a1a1aa] mt-0.5">{desc}</span>
                </span>
              </span>
              <span className={`w-11 h-6 rounded-full p-0.5 transition-colors ${config[key] ? 'bg-[#EF694C]' : 'bg-[#27272a]'}`}>
                <span className={`block w-5 h-5 rounded-full bg-white transition-transform ${config[key] ? 'translate-x-5' : 'translate-x-0'}`} />
              </span>
            </button>
          ))}
        </div>

        <div className="bg-[#121214] border border-white/10 rounded-xl p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Name des KI-Assistenten</label>
            <input
              value={config.assistant_name || 'KI-Assistent'}
              onChange={(e) => updateConfig('assistant_name', e.target.value)}
              data-testid="widget-assistant-name"
              className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Fallback-Antwort ohne Wissensbasis</label>
            <textarea
              value={config.no_context_reply || 'Dazu liegen mir aktuell keine gesicherten Informationen vor. Ich gebe Ihre Anfrage gerne an einen Mitarbeiter weiter.'}
              onChange={(e) => updateConfig('no_context_reply', e.target.value)}
              rows={3}
              data-testid="widget-no-context-reply"
              className="w-full bg-[#09090b] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#EF694C] resize-none"
            />
            <p className="text-xs text-[#71717a] mt-1.5">
              Wird im Widget angezeigt, wenn keine passende Wissensbasis vorhanden ist.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Nachricht, wenn ein Mitarbeiter online ist</label>
            <textarea
              value={config.escalation_online_message || 'Ich verbinde Sie mit einem Mitarbeiter. Bitte bleiben Sie kurz im Chat.'}
              onChange={(e) => updateConfig('escalation_online_message', e.target.value)}
              rows={2}
              data-testid="widget-escalation-online-message"
              className="w-full bg-[#09090b] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#EF694C] resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Nachricht, wenn kein Mitarbeiter online ist</label>
            <textarea
              value={config.escalation_offline_message || 'Vielen Dank. Wir haben Ihre Anfrage erhalten und melden uns schnellstmöglich per E-Mail bei Ihnen.'}
              onChange={(e) => updateConfig('escalation_offline_message', e.target.value)}
              rows={2}
              data-testid="widget-escalation-offline-message"
              className="w-full bg-[#09090b] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#EF694C] resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Nachricht, wenn für Eskalation eine E-Mail benötigt wird</label>
            <textarea
              value={config.escalation_email_required_message || 'Aktuell ist kein Mitarbeiter online. Bitte hinterlassen Sie Ihre E-Mail-Adresse, damit wir Ihre Anfrage beantworten können.'}
              onChange={(e) => updateConfig('escalation_email_required_message', e.target.value)}
              rows={2}
              data-testid="widget-escalation-email-required-message"
              className="w-full bg-[#09090b] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#EF694C] resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Text für Eskalationsbutton</label>
            <input
              value={config.escalation_label || 'Mit einem Mitarbeiter sprechen'}
              onChange={(e) => updateConfig('escalation_label', e.target.value)}
              data-testid="widget-escalation-label"
              className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Widget-Begrüßung</label>
            <input
              value={config.welcome_message || ''}
              onChange={(e) => updateConfig('welcome_message', e.target.value)}
              data-testid="widget-welcome-message"
              className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Offline-Nachricht</label>
            <input
              value={config.offline_message || ''}
              onChange={(e) => updateConfig('offline_message', e.target.value)}
              data-testid="widget-offline-message"
              className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Widget-Grafik / Bild-URL</label>
            <div className="flex gap-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden border border-white/10 shrink-0"
                style={{ backgroundColor: config.theme_color || '#EF694C' }}
              >
                {config.launcher_image_url ? (
                  <img src={config.launcher_image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <MessageSquare className="w-5 h-5 text-white" />
                )}
              </div>
              <input
                value={config.launcher_image_url || ''}
                onChange={(e) => updateConfig('launcher_image_url', e.target.value)}
                placeholder="https://www.onfyre.de/widget-icon.svg"
                data-testid="widget-launcher-image-url"
                className="flex-1 h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C]"
              />
            </div>
            <p className="text-xs text-[#71717a] mt-1.5">
              Leer lassen, um das Standard-Chatbubble-Icon zu verwenden.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">Widget-Farbe</label>
            <div className="flex gap-3">
              <input
                type="color"
                value={config.theme_color || '#EF694C'}
                onChange={(e) => updateConfig('theme_color', e.target.value)}
                data-testid="widget-theme-color-picker"
                className="h-10 w-14 bg-[#09090b] border border-white/10 rounded-lg"
              />
              <input
                value={config.theme_color || '#EF694C'}
                onChange={(e) => updateConfig('theme_color', e.target.value)}
                data-testid="widget-theme-color"
                className="flex-1 h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm font-mono focus:outline-none focus:border-[#EF694C]"
              />
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-widget-settings"
            className="h-10 px-4 rounded-lg bg-[#EF694C] hover:bg-[#d65d43] disabled:opacity-60 text-white text-sm font-semibold"
          >
            {saving ? (t.saving || 'Speichern...') : (t.save || 'Speichern')}
          </button>
        </div>

        <div className="bg-[#121214] border border-white/10 rounded-xl p-6 space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">Widget-Vorschau</h3>
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center overflow-hidden shadow-[0_0_20px_rgba(239,105,76,0.35)]"
              style={{ backgroundColor: config.theme_color || '#EF694C' }}
            >
              {config.launcher_image_url ? (
                <img src={config.launcher_image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <MessageSquare className="w-6 h-6 text-white" />
              )}
            </div>
          </div>

          <div>
            {widgetUrl ? (
              <a
                href={widgetUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-widget-preview"
                className="inline-flex items-center gap-2 text-sm text-[#EF694C] hover:underline"
              >
                {widgetUrl} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : (
              <p className="text-sm text-[#a1a1aa]">Widget-Link konnte noch nicht ermittelt werden.</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-medium text-[#d4d4d8]">Einbettungscode</label>
              <button
                type="button"
                onClick={copyEmbed}
                disabled={!embedCode}
                className="inline-flex items-center gap-1 text-xs text-[#a1a1aa] hover:text-white disabled:opacity-40"
                data-testid="copy-widget-embed"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Kopiert' : 'Kopieren'}
              </button>
            </div>
            <pre className="bg-[#09090b] border border-white/10 rounded-lg p-3 text-xs text-[#a1a1aa] overflow-x-auto">
              {embedCode || 'Kein Einbettungscode verfügbar.'}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
